<script>
  import { types } from '@commercebear/portal-api';
  import CommerceBear from '@commercebear/components/CommerceBear';
  import Logo from '$lib/components/Logo';
  import { IconButton } from '@commercebear/components/Button';
  import useMediaQuery from '@commercebear/components/utils/useMediaQuery';
  
  import Login from '$lib/components/Login.svelte';
  import NavigationSidebar from '$lib/components/NavigationSidebar';
  import OneFlow from '$lib/components/OneFlow';
  import HeapIo from '$lib/components/HeapIo';
  import ChameleonIo from '$lib/components/ChameleonIo/ChameleonIo.svelte';
  import JobsDialogProvider from '$lib/components/JobsDialogProvider';
  import JobStartedTrayProvider from '$lib/components/JobStartedTrayProvider';
  
  import ApiProvider from '$providers/ApiProvider';
  import SseProvider from '$providers/SseProvider';
  import ModelsProvider from '$providers/Models.svelte';
  import CurrentOrganizationProvider from '$providers/CurrentOrganizationProvider';
  import AllOrganizationsProvider from '$providers/AllOrganizationsProvider';
  import IsAdminProvider from '$providers/IsAdmin/IsAdmin.svelte';
  import ChannelsProvider from '$providers/ChannelsProvider';
  import PublishingSidebar from '$lib/components/PublishingSidebar';
  import { getStores } from '$app/stores';
  import Error from './+error.svelte';

  // TODO: we probably won't need this once Projects page is redesigned
  import appWidth from '../stores/appWidth';

  const { page } = getStores();

  const PUBLIC = new Set(['/resetpassword', '/unsubscribe', '/category-mapping']);
  const segment = $page.route?.id;

  const isMobile = useMediaQuery('(max-width: 767px)', false);

  let navSidebar;
  let sidebarPinned = true;

  const showPublishingSidebar = $page.data.session?.features?.includes(
    types.feature.SELF_SERVE_PUBLISHING,
  );
  const onboarding = $page.route?.id === '/onboarding';
</script>

<CommerceBear>
  <ApiProvider>
    {#if $page.data.session?.env.ONE_FLOW_API_KEY}
      <OneFlow />
    {/if}

    {#if $page.data.session?.env.HEAP_IO_API_KEY}
      <HeapIo />
    {/if}

    {#if $page.data.session?.env.CHAMELEON_IO_API_KEY}
      <ChameleonIo />
    {/if}

    {#if PUBLIC.has(segment)}
      <slot />
    {:else if $page.data.session?.user}
      <SseProvider>
        <ModelsProvider>
          <CurrentOrganizationProvider>
            <AllOrganizationsProvider>
              <JobsDialogProvider>
                <JobStartedTrayProvider>
                  <IsAdminProvider>
                    <ChannelsProvider>
                      {#if $isMobile}
                        <header>
                          <a href='/products/status'>
                            <Logo/>
                          </a>
                          <IconButton small icon='menu' on:click={navSidebar.toggle} />
                        </header>
                      {/if}

                      <div class='full-app'>
                        {#if !onboarding}
                          <nav class:pinned={sidebarPinned} class:mobile={$isMobile}>
                            {#if showPublishingSidebar}
                              <PublishingSidebar bind:this={navSidebar} bind:pinned={sidebarPinned} isMobile={$isMobile} />
                            {:else}
                              <NavigationSidebar bind:this={navSidebar} bind:pinned={sidebarPinned} isMobile={$isMobile} />
                            {/if}
                          </nav>
                        {/if}

                        <div class='layout' class:pinned={sidebarPinned} class:mobile={$isMobile || onboarding} bind:clientWidth={$appWidth}>
                          <div class='content'>
                            <!-- Page -->
                            <div class='main-content'>
                              <main class='main'>
                                {#if $page.data.session.error}
                                  <!--
                                    In the case that our session middleware runs into errors, it will populate
                                    $page.data.error. In the case that the page does *not* use the Preloader, we
                                    still need to intercept this error and display it ourselves.

                                    Svelte-kit may still render its own error page if we call `this.error()` during
                                    a preload, in which case it goes to the `<slot />` case below.

                                    In either case, the error should look the same. Users shouldn't need to know
                                    the difference, we just use the _error.svelte component directly here.
                                  -->
                                  <Error status={$page.data.session.error.status} error={$page.data.session.error} />
                                {:else}
                                  <slot />
                                {/if}
                              </main>
                            </div>
                          </div>
                        </div>
                      </div>
                    </ChannelsProvider>
                  </IsAdminProvider>
                </JobStartedTrayProvider>
              </JobsDialogProvider>
            </AllOrganizationsProvider>
          </CurrentOrganizationProvider>
        </ModelsProvider>
      </SseProvider>
    {:else if $page.error && $page.data.session?.user}
      <!-- TODO: We need some BrandError page? -->
      <slot />
    {:else}
      <Login testid='login-page' />
    {/if}
  </ApiProvider>
</CommerceBear>

<style lang="postcss">
  header {
    --Logo--size: 36px;

    height: 70px;
    display: flex;
    align-items: center;
    gap: var(--spacing--xl);
    background: var(--color--white);
    padding-left: var(--spacing--m);
    position: relative;
    z-index: 2;
  }

  nav {
    width: 60px;

    &.pinned { 
      width: 240px;
    }

    &.mobile {
      width: 0;
    }
  }

  .full-app {
    display: flex;
    height: 100%;
    width: 100%;
  }

  .layout {
    display: flex;
    justify-content: stretch;
    height: 100%;
    width: calc(100% - 60px);
    background-color: var(--color--grey-5);

    &.pinned { 
      width: calc(100% - 240px);
    }

    &.mobile {
      width: 100%;
    }
  }

  .content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .main-content {
    display: flex;
    flex: 1;
    margin: var(--spacing--m);
    overflow-y: auto;
  }

  .main {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow-y: auto;
  }
</style>
